'use strict';

/**
 * ProductForm custom element.
 * Handles variant selection: updates hidden input, URL, price, button state, and gallery.
 * Registered as <product-form> in sections/product-main.liquid.
 */
/**
 * Same output as Liquid's `money_without_trailing_zeros` with the shop's money format
 * (window.Pippei.moneyFormat, set in layout/theme.liquid): decimals only when the amount has øre.
 */
function formatMoney(cents, plain) {
  var format = (window.Pippei && window.Pippei.moneyFormat) || '{{amount_with_comma_separator}} kr';
  var placeholder = /\{\{\s*(\w+)\s*\}\}/;
  var whole = cents % 100 === 0;

  function group(value, precision, thousands, decimal) {
    var parts = (value / 100).toFixed(precision).split('.');
    var units = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    return parts[1] ? units + decimal + parts[1] : units;
  }

  var match = format.match(placeholder);
  var key = match ? match[1] : 'amount_with_comma_separator';
  var p = whole ? 0 : 2;
  var value;
  switch (key) {
    case 'amount':                                  value = group(cents, p, ',', '.'); break;
    case 'amount_no_decimals':                      value = group(cents, 0, ',', '.'); break;
    case 'amount_with_comma_separator':             value = group(cents, p, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator': value = group(cents, 0, '.', ','); break;
    case 'amount_with_space_separator':             value = group(cents, p, ' ', ','); break;
    case 'amount_no_decimals_with_space_separator': value = group(cents, 0, ' ', ','); break;
    case 'amount_with_apostrophe_separator':        value = group(cents, p, "'", '.'); break;
    default:                                        value = group(cents, p, '.', ',');
  }
  if (plain) return value;   // the number only («129,-» style, snippets/money-plain.liquid)
  return match ? format.replace(placeholder, value) : value + ' kr';
}

function escapeHtml(text) {
  return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

class ProductForm extends HTMLElement {
  connectedCallback() {
    // The info column is built from theme blocks; any of them may be missing or reordered.
    this.galleryTrack = document.getElementById('gallery-track');
    this._setupGallery();

    var dataEl = document.getElementById('variant-data');
    if (!dataEl) return;
    this.variants = JSON.parse(dataEl.textContent);

    this.form     = this.querySelector('form[data-product-form]');
    this.priceEls = this.querySelectorAll('[data-product-price]');

    this.querySelectorAll('[data-option]').forEach(function (el) {
      el.addEventListener('change', this.onVariantChange.bind(this));
    }, this);
    this._syncSoldOut();

    // Pre-apply variant from URL param on page load
    var params = new URLSearchParams(window.location.search);
    var preId  = parseInt(params.get('variant'), 10);
    if (preId) {
      var pre = this.variants.find(function (v) { return v.id === preId; });
      if (pre) this.applyVariant(pre);
    }
  }

  _setupGallery() {
    var track = this.galleryTrack;
    if (!track) return;

    // Update dot indicators as user swipes
    track.addEventListener('scroll', function () {
      var slides = track.querySelectorAll('[data-image-id]');
      if (!slides.length) return;
      var center = track.scrollLeft + track.offsetWidth / 2;
      var activeIdx = 0;
      slides.forEach(function (slide, i) {
        if (slide.offsetLeft <= center) activeIdx = i;
      });
      document.querySelectorAll('#gallery-dots span').forEach(function (dot, i) {
        dot.classList.toggle('bg-bark', i === activeIdx);
        dot.classList.toggle('bg-sand', i !== activeIdx);
      });
      var activeId = slides[activeIdx] && slides[activeIdx].getAttribute('data-image-id');
      document.querySelectorAll('[data-thumbnail-id]').forEach(function (btn) {
        btn.setAttribute('aria-current', String(btn.getAttribute('data-thumbnail-id') === activeId));
      });
    }, { passive: true });

    // The page may open on another slide than the first (start image setting): sync counter, thumbnails and dots once.
    track.dispatchEvent(new Event('scroll'));

    // Desktop thumbnail clicks → scroll gallery to that image
    document.querySelectorAll('[data-thumbnail-id]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var slide = track.querySelector('[data-image-id="' + btn.dataset.thumbnailId + '"]');
        if (slide) slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      });
    });
  }

  getCurrentOptions() {
    var options = [];
    for (var i = 1; i <= 3; i++) {
      var radios = this.querySelectorAll('[data-option="' + i + '"]');
      if (!radios.length) break;
      if (radios[0].tagName === 'SELECT') {
        options.push(radios[0].value);
      } else {
        var checked = this.querySelector('[data-option="' + i + '"]:checked');
        if (checked) options.push(checked.value);
      }
    }
    return options;
  }

  onVariantChange() {
    var options = this.getCurrentOptions();
    var variant = this.variants.find(function (v) {
      return v.options.every(function (opt, idx) { return opt === options[idx]; });
    });
    if (variant) this.applyVariant(variant);
    this._syncSoldOut();
  }

  // A value is crossed out (data-soldout) when the variant it would give, with the other options as chosen now, is sold out or missing.
  // The value stays clickable: the sold-out variant opens the notify-me form («Tilbake på lager»).
  _syncSoldOut() {
    var self = this;
    var current = this.getCurrentOptions();
    var soldText = (window.PippeiStrings && window.PippeiStrings.soldOut) || 'utsolgt';
    function soldOut(idx, value) {
      var opts = current.slice();
      opts[idx] = value;
      var hit = self.variants.find(function (v) {
        return v.options.every(function (o, i) { return o === opts[i]; });
      });
      return !hit || !hit.available;
    }
    this.querySelectorAll('input[type="radio"][data-option]').forEach(function (inp) {
      var span = inp.nextElementSibling;
      if (!span) return;
      var so = soldOut(parseInt(inp.getAttribute('data-option'), 10) - 1, inp.value);
      span.toggleAttribute('data-soldout', so);
      var t = span.querySelector('[data-so-text]');
      if (t) t.textContent = so ? ' (' + soldText + ')' : '';
    });
    this.querySelectorAll('select[data-option]').forEach(function (sel) {
      var idx = parseInt(sel.getAttribute('data-option'), 10) - 1;
      [].forEach.call(sel.options, function (o) {
        var base = o.textContent.replace(/\s*\(.*\)\s*$/, '').trim();
        var so = soldOut(idx, o.value);
        o.toggleAttribute('data-soldout', so);
        o.textContent = so ? base + ' (' + soldText + ')' : base;
      });
    });
  }

  applyVariant(variant) {
    // 1. Update hidden id input
    var idInput = this.form ? this.form.querySelector('input[name="id"]') : null;
    if (idInput) idInput.value = variant.id;

    // 2. Update URL without reload
    var url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());

    // 3. Update price display
    if (this.priceEls.length) {
      this.priceEls.forEach(function (el) {
        // «129,-» style (snippets/price.liquid with plain): the number with the texts before and after instead of the currency
        var plain = el.hasAttribute('data-price-plain');
        var before = escapeHtml(el.getAttribute('data-price-before') || '');
        var after = escapeHtml(el.getAttribute('data-price-after') || '');
        function fmt(cents) { return plain ? before + formatMoney(cents, true) + after : formatMoney(cents); }
        var html = '';
        if (variant.compare_at_price > variant.price) {
          html  = '<span class="font-semibold text-terrakotta">' + fmt(variant.price) + '</span>';
          html += ' <span class="text-bark-muted line-through text-sm">' + fmt(variant.compare_at_price) + '</span>';
        } else {
          html = '<span class="font-semibold text-brand">' + fmt(variant.price) + '</span>';
        }
        el.innerHTML = html;
      });
    }

    // 3a. Heading: short title per variant (blocks/product-title.liquid), only when the map exists
    this.querySelectorAll('[data-product-title]').forEach(function (el) {
      var data = el.parentElement.querySelector('[data-product-titles]');
      if (!data) return;
      var map = {};
      try { map = JSON.parse(data.textContent); } catch (e) { return; }
      var text = map[String(variant.id)];
      if (text) el.innerHTML = text;   // Liquid already escaped the short titles
    });

    // 3a2. Short points per variant (blocks/product-title.liquid): pre-rendered <li> markup per variant
    this.querySelectorAll('[data-product-points]').forEach(function (el) {
      var data = el.parentElement.querySelector('[data-product-points-map]');
      if (!data) return;
      var map = {};
      try { map = JSON.parse(data.textContent); } catch (e) { return; }
      var html = map[String(variant.id)] || '';
      el.innerHTML = html;   // built and escaped in Liquid
      el.hidden = !html;
    });

    // 3b. Member price (blocks/product-price.liquid): pre-formatted per variant, absent = not shown
    this.querySelectorAll('[data-member-price]').forEach(function (el) {
      var map = {};
      var data = el.parentElement.querySelector('[data-member-prices]');
      try { map = JSON.parse(data ? data.textContent : '{}'); } catch (e) { map = {}; }
      var text = map[String(variant.id)];
      el.hidden = !text;
      var value = el.querySelector('[data-member-price-value]');
      if (value) value.textContent = text || '';
    });

    // 4. Buy control (assets/buy-control.js) updates the button, quantity limits, total and helper line
    this.dispatchEvent(new CustomEvent('pippei:variant-change', { bubbles: true, detail: { variant: variant } }));

    // 5. Scroll gallery to the slide matching this variant's featured image
    //    A variant with its own jump number (pippei.hopp_til_bilde) goes to that image instead.
    var jumpData = document.querySelector('[data-variant-jumps]');
    var jumps = {};
    if (jumpData) { try { jumps = JSON.parse(jumpData.textContent); } catch (e) { jumps = {}; } }
    var jumpId = jumps[String(variant.id)];
    if ((jumpId || variant.featured_image) && this.galleryTrack) {
      var slideId = String(jumpId || variant.featured_image.id);
      var slide = this.galleryTrack.querySelector('[data-image-id="' + slideId + '"]');
      if (slide) slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  }
}

customElements.define('product-form', ProductForm);
